import {
  CSSProperties,
  ReactElement,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import styled, { StyledComponent } from "styled-components";
import { useTabularMenuContext } from "./TabularMenuContext";
import { primaryActive } from "../../typography/alpha";
import { renderToString } from "react-dom/server";

type StyledTabularMenuProps = {
  largestTabWidth: number;
};

const StyledTabularMenuHorizontal = styled.div<StyledTabularMenuProps>`
  position: relative;
  display: flex;
  > button {
    min-width: ${(props) => props.largestTabWidth}px;
  }
`;

const StyledTabularMenuVertical = styled.div<StyledTabularMenuProps>`
  position: relative;
  display: inline-flex;
  > button {
    min-width: ${(props) => props.largestTabWidth}px;
  }
  flex-direction: column;
`;

const TabHorizontal = styled.button`
  position: relative;
  cursor: pointer;
  box-sizing: border-box;
  min-height: 32px;
  line-height: 16px;
  margin: 0px;
  padding: 8px;
  padding-right: 46px;
  text-align: left;
  ${primaryActive.subtitle3};
  background: var(--color-neutral-000-full);
  border: none;
  border-bottom: 2px solid var(--color-primary-200);
  transition: background-color 0.2s ease-in-out;
  :after {
    content: "";
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 2px;
    background: var(--color-primary-400);
    transform: scaleX(0);
    transition: 0.2s ease-in-out transform;
  }
  :focus {
    outline: none;
  }
  :hover,
  :focus {
    &:after {
      transform: scaleX(1);
    }
  }
  &.transparent {
    background-color: transparent;
  }
  /* stylelint-disable no-descending-specificity */
  &.active {
    cursor: default;
    background: var(--color-secondary-200);
    :after {
      display: none;
    }
  }
  /* stylelint-enable no-descending-specificity */
`;

const TabVertical = styled.button`
  position: relative;
  cursor: pointer;
  box-sizing: border-box;
  min-height: 32px;
  line-height: 16px;
  margin: 0px;
  padding: 8px;
  padding-right: 46px;
  text-align: left;
  ${primaryActive.subtitle3};
  color: var(--color-primary-900);
  font-weight: 500;

  background: var(--color-neutral-000-full);
  border: none;
  border-left: 3px solid rgba(0, 0, 0, 0);
  transition: background-color 0.2s ease-in-out;
  /* stylelint-disable no-descending-specificity */
  :after {
    content: "";
    position: absolute;
    left: -3px;
    top: 0;
    bottom: 0;
    width: 3px;
    background: var(--color-primary-400);
    transform: scaleX(0);
    transition: 0.2s ease-in-out transform;
  }
  /* stylelint-enable no-descending-specificity */
  :focus {
    outline: none;
  }
  :hover,
  :focus {
    &:after {
      transform: scaleX(1);
    }
  }
  &.transparent {
    background-color: transparent;
  }
  /* stylelint-disable no-descending-specificity */
  &.active {
    cursor: default;
    background: var(--color-secondary-200);
    font-weight: 600;
    :after {
      display: none;
    }
  }
  /* stylelint-enable no-descending-specificity */
`;

const ActiveIndicatorHorizontal = styled.div`
  position: absolute;
  bottom: 0;
  height: 2px;
  background: var(--color-secondary-600);
  transition: left 0.2s ease-in-out, width 0.2s ease-in-out;
`;

const ActiveIndicatorVertical = styled.div`
  position: absolute;
  left: 0;
  width: 3px;
  background: var(--color-secondary-600);
  transition: top 0.2s ease-in-out, height 0.2s ease-in-out;
`;

type useLargestTabWidthArgs = {
  menuElement: HTMLDivElement | null;
};

const useLargestTabWidth = ({ menuElement }: useLargestTabWidthArgs) => {
  const [largestTabWidth, setLargestTabWidth] = useState(0);
  /* eslint-disable react-hooks/exhaustive-deps */
  useLayoutEffect(() => {
    if (!menuElement) {
      return;
    }
    const tabs = Array.from(menuElement.children);
    const tabWidths = tabs.map((tab) => {
      const { width } = window.getComputedStyle(tab);
      const widthWithoutUnit = width.substring(0, width.length - 2);
      return Number.parseInt(widthWithoutUnit, 10);
    });
    setLargestTabWidth(Math.max(...tabWidths));
  });
  /* eslint-enable react-hooks/exhaustive-deps */
  return {
    largestTabWidth,
  };
};

const useActiveIndicatorStyles = (
  activeTabIndex: number,
  tabOrientation: "horizontal" | "vertical",
  largestTabWidth: number,
  tabCount: number
) => {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [styles, setStyles] = useState<CSSProperties>({});

  useLayoutEffect(() => {
    const activeTabElement = tabRefs.current[activeTabIndex];
    if (!activeTabElement) {
      return;
    }
    setStyles(
      tabOrientation === "vertical"
        ? {
            top: `${activeTabElement.offsetTop}px`,
            height: `${activeTabElement.offsetHeight}px`,
          }
        : {
            left: `${activeTabElement.offsetLeft}px`,
            width: `${activeTabElement.offsetWidth}px`,
          }
    );
  }, [activeTabIndex, tabOrientation, largestTabWidth, tabCount]);

  return { tabRefs, styles };
};

type TabularMenuProps = {
  tabOrientation?: "horizontal" | "vertical";
  className?: string;
  testId?: string;
  id?: string;
  transparentTabs?: boolean;
};

export enum TabularMenuTestIds {
  Container = "tabular-menu",
}

export const TabularMenu = ({
  tabOrientation = "horizontal",
  className,
  testId,
  id,
  transparentTabs,
}: TabularMenuProps): JSX.Element => {
  const { tabs, activeTabIndex, setActiveTabIndex } = useTabularMenuContext();
  const [menuElement, setMenuElement] = useState<HTMLDivElement | null>(null);
  const { largestTabWidth } = useLargestTabWidth({ menuElement });
  const { tabRefs, styles: activeIndicatorStyles } = useActiveIndicatorStyles(
    activeTabIndex,
    tabOrientation,
    largestTabWidth,
    tabs.length
  );

  let StyledTabularMenu: StyledComponent<
    "div",
    Record<string, unknown>,
    StyledTabularMenuProps
  >;
  let Tab: StyledComponent<"button", Record<string, unknown>>;
  let ActiveIndicator: StyledComponent<"div", Record<string, unknown>>;

  if (tabOrientation === "vertical") {
    StyledTabularMenu = StyledTabularMenuVertical;
    Tab = TabVertical;
    ActiveIndicator = ActiveIndicatorVertical;
  } else {
    StyledTabularMenu = StyledTabularMenuHorizontal;
    Tab = TabHorizontal;
    ActiveIndicator = ActiveIndicatorHorizontal;
  }

  return (
    <StyledTabularMenu
      largestTabWidth={largestTabWidth}
      ref={setMenuElement}
      className={className}
      data-testid={testId ?? TabularMenuTestIds.Container}
      id={id}
    >
      {tabs.map((tab, index) => {
        const tabIsActive = index === activeTabIndex;
        const onClick = tabIsActive
          ? () => {}
          : () => {
              if (tab.onClick) {
                tab.onClick();
              }
              setActiveTabIndex(index);
            };

        const tabClassNames = [
          tabIsActive ? "active" : "",
          !tabIsActive && transparentTabs ? "transparent" : "",
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <Tab
            id={tab?.id}
            key={renderToString(tab.label as ReactElement)}
            ref={(el: HTMLButtonElement | null) => {
              tabRefs.current[index] = el;
            }}
            onClick={onClick}
            className={tabClassNames}
          >
            {tab.label as JSX.Element}
          </Tab>
        );
      })}
      <ActiveIndicator style={activeIndicatorStyles} />
    </StyledTabularMenu>
  );
};
